// libraries.
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

// slices.
import { apiBudgetRoute } from "../apiSlices";

// interfaces.
import { ModelError } from "../interfaces/interfaces";
import {
  ExceptionRule,
  IncentiveStep,
} from "@pages/RulesPage/modules/RulesMain/rulesData";
import {
  RawCondition,
  RawConditionComplex,
  RawConditionSimple,
  RawAction,
  RawRuleItem,
  RawRulesApiResponse,
  RulesSchema,
} from "./interfaces/rawRules.interfaces";

// ─── Transformed types (stored in slice) ─────────────────────────────────────

export interface RulesApiData {
  exceptions: ExceptionRule[];
  steps: IncentiveStep[];
  rawRules: RawRuleItem[];
}

export interface RulesTransformedResponse {
  data: RulesApiData;
  message: string;
  success: boolean;
}

// ─── Transform helpers ────────────────────────────────────────────────────────

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  apuesta: "Apuesta",
  categorica_grid: "Categoría Grid",
  desistimiento_ultimos_15_dias: "Desistimiento últimos 15 días",
  v_price: "Valor de compra",
};

const isComplexCondition = (c: RawCondition): c is RawConditionComplex =>
  "conditions" in c;

const isDiasField = (left: unknown): boolean =>
  typeof left === "string" && left.includes("dias_full");

const extractDayRange = (item: RawRuleItem): [number, number | null] => {
  const { rule, rule_id } = item;

  // Preventa uses tipo_publicacion, not dias_full
  if (rule_id.includes("preventa")) return [0, 89];

  // Simple condition: single dias_full check (e.g. dias_full >= 60)
  if (!isComplexCondition(rule.condition)) {
    const c = rule.condition;
    if (!isDiasField(c.left)) return [0, null];
    const val = c.right as number;
    if (c.type === "greater_or_equal" || c.type === "greater_than") return [val, null];
    if (c.type === "less_than")   return [0, val - 1];
    if (c.type === "less_or_equal") return [0, val];
    if (c.type === "equals")      return [val, val];
    return [0, null];
  }

  // Compound condition (AND/OR): extract all dias_full sub-conditions
  const diasConditions = rule.condition.conditions.filter(
    (c) => isDiasField(c.left),
  );

  // Exact day: dias_full = 60 → [60, 60]
  const equalsCond = diasConditions.find((c) => c.type === "equals");
  if (equalsCond) {
    const val = equalsCond.right as number;
    return [val, val];
  }

  const min = (
    diasConditions.find((c) => c.type === "greater_or_equal")?.right ?? 0
  ) as number;

  const maxCond = diasConditions.find((c) => c.type === "less_than");
  // Subtract 1 to get the last inclusive day (e.g. < 210 → ends at 209)
  const max = maxCond ? ((maxCond.right as number) - 1) : null;

  return [min, max];
};

const extractCategory = (
  ruleId: string,
  actions: RawAction[],
): IncentiveStep["category"] => {
  if (ruleId.includes("preventa")) return "transicion";
  if (
    ruleId.includes("liquidacion") ||
    ruleId === "colombia_dia_280" ||
    ruleId === "mexico_dia_300"
  )
    return "remate";
  const hasDescuento = actions.some((a) => a.tool === "descuentos");
  return hasDescuento ? "descuento" : "bono";
};

const extractAdjustment = (ruleId: string, actions: RawAction[]): string => {
  if (ruleId.includes("liquidacion")) return "Descuento liquidación";
  if (ruleId === "colombia_dia_280" || ruleId === "mexico_dia_300")
    return "Valor de compra";

  const descuentoAction = actions.find((a) => a.tool === "descuentos");
  if (descuentoAction) {
    const pct = (descuentoAction.params.percentage as number) * 100;
    return `-${pct}%`;
  }

  const bonoAction = actions.find((a) => a.tool === "api_bonos");
  if (bonoAction) {
    const pct = (bonoAction.params.percentage as number) * 100;
    return `Bono ${pct}%`;
  }

  return "";
};

const mapToIncentiveStep = (item: RawRuleItem): IncentiveStep => {
  const { rule } = item;
  return {
    id: item.rule_id,
    name: rule.name,
    description: rule.description,
    dayRange: extractDayRange(item),
    category: extractCategory(item.rule_id, rule.actions),
    adjustment: extractAdjustment(item.rule_id, rule.actions),
    modifiesPrice: rule.actions.some(
      (a) => a.tool === "descuentos" || a.tool === "calculadora",
    ),
    details: rule.description,
  };
};

const mapToExceptionRule = (item: RawRuleItem): ExceptionRule => {
  const { rule } = item;
  const condition = rule.condition as RawConditionSimple;
  const rawField = condition.left.replace(/\$\{|\}/g, "");
  return {
    id: item.rule_id,
    name: rule.name,
    description: rule.description,
    field: FIELD_DISPLAY_NAMES[rawField] ?? rawField,
    triggerValue: condition.right as number,
  };
};

export const transformRulesResponse = (
  raw: RawRulesApiResponse,
): RulesTransformedResponse => {
  const sorted = [...raw.data.rules].sort(
    (a, b) => a.rule.priority - b.rule.priority,
  );

  const exceptions = sorted
    .filter((item) => item.rule_id.startsWith("excepcion_"))
    .map(mapToExceptionRule);

  const steps = sorted
    .filter((item) => !item.rule_id.startsWith("excepcion_"))
    .map(mapToIncentiveStep);

  return {
    data: { exceptions, steps, rawRules: sorted },
    message: raw.message,
    success: raw.success,
  };
};

// ─── Endpoint ─────────────────────────────────────────────────────────────────

interface GetRulesParams {
  schema: RulesSchema;
}

export const rulesEndpoints = apiBudgetRoute.injectEndpoints({
  endpoints: (builder) => ({
    getRules: builder.query<RulesTransformedResponse, GetRulesParams>({
      query: ({ schema }) => `/rules?schema=${schema}`,
      providesTags: (_result, _error, { schema }) => [{ type: "Rules", id: schema }],
      transformErrorResponse: (error: FetchBaseQueryError) => {
        const modelError: ModelError = {
          errorStatus: error.status,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          messageFromServer: (error.data as any)?.message,
          serviceErrorName: "getRules",
        };
        return modelError;
      },
      transformResponse: (raw: RawRulesApiResponse) =>
        transformRulesResponse(raw),
    }),
  }),
});

export const { useGetRulesQuery, useLazyGetRulesQuery } = rulesEndpoints;
